import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';
import { Inbox } from 'lucide-react';
import { Card } from '../ui/Card';
import { WIDGET_HEADER_FLUSH, WidgetCardHeader } from '../dashboard/WidgetCardHeader';
import { Spinner } from '../ui/Spinner';
import { TicketStatusBadge } from './TicketStatusBadge';
import { getDisplayStatus } from './ticketStatusDisplay';
import { useTickets, useTicketCounts } from '../../hooks/queries/useTickets';
import { useAuthStore } from '../../store/useAuthStore';
import { useIsAdminOrSecretary } from '../../hooks/usePermissions';
import { revealSlice } from '../../services/dashboard/ordreDuJour';
import { WidgetInfiniteList } from '../dashboard/WidgetInfiniteList';

export function TicketsSummaryWidget() {
  const navigate = useNavigate();
  const collaborator = useAuthStore((s) => s.collaborator);
  const isAdminOrSecretary = useIsAdminOrSecretary();

  // Lawyers only see their own; admin/secretary see all
  const assignedToFilter: string | undefined = isAdminOrSecretary
    ? undefined
    : (collaborator?.id ?? undefined);

  const { data: recentTickets, isLoading } = useTickets({
    status: ['new', 'in_progress'],
    assigned_to: assignedToFilter,
  });

  const { data: counts } = useTicketCounts();

  const openTickets = recentTickets ?? [];

  return (
    <Card
      className="h-full"
      headerClassName={WIDGET_HEADER_FLUSH}
      header={
        <WidgetCardHeader icon={Inbox} title="Demandes cette semaine" href="/tickets" />
      }
    >
      {/* Stats row */}
      {counts && (
        <div className="flex items-center gap-4 mb-4 pb-3 border-b border-neutral-100">
          <div className="text-center">
            <p className="text-lg font-bold text-red-600">{counts.new}</p>
            <p className="text-xs text-neutral-400">Nouveau{counts.new > 1 ? 'x' : ''}</p>
          </div>
          <button
            type="button"
            onClick={() => navigate('/tickets?awaiting=1')}
            className="text-center cursor-pointer hover:opacity-80 transition-opacity"
            title="Demandes qui vous sont assignées et qui attendent votre prise en charge"
          >
            <p className="text-lg font-bold text-amber-700">{counts.awaiting_claim}</p>
            <p className="text-xs text-neutral-400">En attente</p>
          </button>
          <div className="text-center">
            <p className="text-lg font-bold text-green-600">{counts.in_progress}</p>
            <p className="text-xs text-neutral-400">Pris en charge</p>
          </div>
        </div>
      )}

      {/* Ticket list */}
      {isLoading ? (
        <div className="flex justify-center py-4">
          <Spinner />
        </div>
      ) : openTickets.length === 0 ? (
        <p className="text-sm text-neutral-400 text-center py-3">
          Aucun ticket ouvert
          {isAdminOrSecretary ? '' : ' assigné à vous'}
        </p>
      ) : (
        <WidgetInfiniteList itemCount={openTickets.length}>
          {(visibleCount) => (
            <ul className="space-y-2">
              {revealSlice(openTickets, visibleCount).items.map((ticket) => {
                const timeAgo = formatDistanceToNow(new Date(ticket.created_at), {
                  addSuffix: true,
                  locale: fr,
                });

                return (
                  <li key={ticket.id}>
                    <button
                      type="button"
                      onClick={() => navigate(`/tickets/${ticket.id}`)}
                      className="w-full flex items-center justify-between gap-3 text-left py-1.5 hover:bg-neutral-50 rounded px-1 transition-colors"
                    >
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-medium text-neutral-700 truncate">
                          {ticket.sender_name ?? 'Inconnu'}
                        </p>
                        <p className="text-xs text-neutral-400 truncate">
                          {ticket.message.slice(0, 60)}
                          {ticket.message.length > 60 ? '…' : ''}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <TicketStatusBadge status={getDisplayStatus(ticket)} />
                        <span className="text-xs text-neutral-400 hidden sm:inline">{timeAgo}</span>
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </WidgetInfiniteList>
      )}
    </Card>
  );
}

